import { queryKeys, useHttpClient } from "@repo/api-client/react";
import {
  ClientSchema,
  type Client,
  type UpdateClientRequest,
} from "@repo/api-client/dtos";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDebouncedAsyncCallback } from "../internal/useDebouncedAsyncCallback";
import { patchJSON } from "../internal/http";

export type UpdateClientVariables = {
  organizationId: string;
  id: string;
  updates: UpdateClientRequest;
};

export function useUpdateClient() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ organizationId, id, updates }: UpdateClientVariables) =>
      patchJSON(
        http,
        `/orgs/${organizationId}/clients/${id}`,
        updates,
        ClientSchema,
      ),
    onMutate: async (variables) => {
      const detailKey = queryKeys.clients.detail(
        variables.organizationId,
        variables.id,
      );
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<Client>(detailKey);
      if (previous) {
        queryClient.setQueryData<Client>(detailKey, {
          ...previous,
          ...variables.updates,
        });
      }
      return { previous };
    },
    onError: (_err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.clients.detail(variables.organizationId, variables.id),
          context.previous,
        );
      }
    },
    onSuccess: (client) => {
      queryClient.setQueryData(
        queryKeys.clients.detail(client.orgId, client.id),
        client,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.all(client.orgId),
      });
    },
  });

  return {
    updateClient: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Debounced wrapper around `updateClient`. Returns
 * `{ call, flush, retry, pending, error, succeededAt }` — see
 * `useDebouncedUpdateOrganization`
 * for the contract and Phase 6 / plan.md H1 for the rationale.
 */
export function useDebouncedUpdateClient(delayMs = 1000) {
  const { updateClient } = useUpdateClient();
  return useDebouncedAsyncCallback(
    (vars: UpdateClientVariables) => updateClient(vars),
    delayMs,
  );
}
